import * as Yup from 'yup';
import CycleMeasurement from '../enums/cycle-measurement';
import { POST_WELD_SETTINGS_LIMITS } from '../components/PostWeldSettings/constants';
import { PREHEAT_SETTINGS_LIMITS } from '../components/PreHeatSettings/constants';
import i18n from '../infrastructure/i18n';
import { LATIN_ALPHANUMERIC_WITH_SPACES_REGEX } from '../constants/common';
import { convertAbsoluteTemperature, convertTemperatureDelta } from './temperature-unit-conversion';

const nameSchema = Yup.string()
  .required(i18n.t('errors.requiredValue'))
  .min(
    POST_WELD_SETTINGS_LIMITS.RECIPE_NAME.MIN,
    i18n.t('errors.stringValue.min', '', {
      maxChars: POST_WELD_SETTINGS_LIMITS.RECIPE_NAME.MIN,
      value: i18n.t('cycle.settings.postWeld.recipeName'),
    }),
  )
  .max(
    POST_WELD_SETTINGS_LIMITS.RECIPE_NAME.MAX,
    i18n.t('errors.stringValue.max', '', {
      maxChars: POST_WELD_SETTINGS_LIMITS.RECIPE_NAME.MAX,
      value: i18n.t('cycle.settings.postWeld.recipeName'),
    }),
  )
  .matches(LATIN_ALPHANUMERIC_WITH_SPACES_REGEX, i18n.t('errors.stringValue.sanitizeAlphaNumericChars'));

const getLimitByMeasurement = (
  celsiusValue: number,
  measurement: CycleMeasurement,
  isAbsoluteTemperature = false,
): number => {
  const converted = isAbsoluteTemperature
    ? convertAbsoluteTemperature(celsiusValue, CycleMeasurement.Celsius, measurement)
    : convertTemperatureDelta(celsiusValue, CycleMeasurement.Celsius, measurement);

  return Number(converted);
};

const temperatureNumberSchema = ({
  MIN,
  MAX,
  measurement,
  isAbsoluteTemperature = false,
}: {
  MIN: number;
  MAX: number;
  measurement: CycleMeasurement;
  isAbsoluteTemperature?: boolean;
}) => {
  const convertedMin = getLimitByMeasurement(MIN, measurement, isAbsoluteTemperature);
  const convertedMax = getLimitByMeasurement(MAX, measurement, isAbsoluteTemperature);

  return Yup.number()
    .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
    .min(convertedMin, i18n.t('errors.numberValue.min', '', { minNum: convertedMin }))
    .max(convertedMax, i18n.t('errors.numberValue.max', '', { maxNum: convertedMax }))
    .required(i18n.t('errors.requiredValue'));
};

// Tolerance fields use a fixed 0–1000 range in the controller's native units.
// When the contract and controller use different units, the safe maximum in the
// contract (display) unit must be adjusted so the converted value never exceeds 1000.
//
// The only case that reduces the maximum is Contract=Celsius + Controller=Fahrenheit:
//   user enters X°C → sent as X × 9/5°F  →  must keep X × 9/5 ≤ 1000
//   ⇒ max X = floor(1000 × 5/9) = 555°C
//
// All other combinations stay at 1000 in the display unit:
//   Contract=F + Controller=C: user enters X°F → sent as X × 5/9°C ≤ 1000  (1000°F → 556°C ✓)
//   Both same units: no conversion, 1000 stays 1000 ✓
const CONTROLLER_TOLERANCE_LIMIT = { MIN: 0, MAX: 1000 };
const toleranceNumberSchema = (
  contractMeasurement?: CycleMeasurement,
  controllerMeasurement?: CycleMeasurement | null,
) => {
  let max = CONTROLLER_TOLERANCE_LIMIT.MAX;
  if (
    controllerMeasurement === CycleMeasurement.Fahrenheit &&
    contractMeasurement === CycleMeasurement.Celsius
  ) {
    max = Math.floor(CONTROLLER_TOLERANCE_LIMIT.MAX * 5 / 9); // 555°C
  }
  return Yup.number()
    .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
    .min(CONTROLLER_TOLERANCE_LIMIT.MIN, i18n.t('errors.numberValue.min', '', { minNum: CONTROLLER_TOLERANCE_LIMIT.MIN }))
    .max(max, i18n.t('errors.numberValue.max', '', { maxNum: max }))
    .required(i18n.t('errors.requiredValue'));
};

export const createPostWeldRecipeFieldValidationSchema = (
  measurement: CycleMeasurement,
  actionField: string,
  editableKey: string,
  controllerMeasurement?: CycleMeasurement | null,
) => {
  if (editableKey === 'name') {
    return Yup.object({
      [actionField]: nameSchema,
    });
  }

  return Yup.object({
    [actionField]: toleranceNumberSchema(measurement, controllerMeasurement),
  });
};

export const createPreHeatRecipeFieldValidationSchema = (
  measurement: CycleMeasurement,
  actionField: string,
  editableKey: string,
  controllerMeasurement?: CycleMeasurement | null,
) => {
  const isToleranceField = editableKey === 'soakUpper' || editableKey === 'soakLower';

  return Yup.object({
    [actionField]: isToleranceField
      ? toleranceNumberSchema(measurement, controllerMeasurement)
      : temperatureNumberSchema({
          ...(editableKey === 'rate' ? PREHEAT_SETTINGS_LIMITS.RATE : PREHEAT_SETTINGS_LIMITS.FINAL_TEMPERATURE),
          measurement,
          isAbsoluteTemperature: editableKey === 'finalTemp',
        }),
  });
};

export const createPostWeldRecipeValidationSchema = (_measurement: CycleMeasurement) =>
  Yup.object({
    name: nameSchema,
    mode: Yup.string().required('Required'),
    rateUpperTolerance: toleranceNumberSchema(),
    rateLowerTolerance: toleranceNumberSchema(),
    holdUpperTolerance: toleranceNumberSchema(),
    holdLowerTolerance: toleranceNumberSchema(),
  });

export const createPreHeatRecipeValidationSchema = (measurement: CycleMeasurement) =>
  Yup.object({
    name: nameSchema,
    mode: Yup.string().required('Required'),
    rate: temperatureNumberSchema({
      ...PREHEAT_SETTINGS_LIMITS.RATE,
      measurement,
    }),
    finalTemp: temperatureNumberSchema({
      ...PREHEAT_SETTINGS_LIMITS.FINAL_TEMPERATURE,
      measurement,
      isAbsoluteTemperature: true,
    }),
    soakUpper: toleranceNumberSchema(),
    soakLower: toleranceNumberSchema(),
  });
